import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAccountSectionIds } from '../components/modals/AccountSettingsModal';
import { getProjectSectionIds } from '../components/modals/ProjectSettingsModal';
import { useAuth } from './AuthContext';

type OrgSettingsSection = 'members' | 'integrations';

interface SettingsModalContextType {
  isProjectSettingsOpen: boolean;
  isAccountSettingsOpen: boolean;
  isProfileModalOpen: boolean;
  currentWorkspaceId: string | null;
  openProjectSettings: (workspaceId?: string, section?: string) => void;
  initialSection: string | null;
  openAccountSettings: (section?: string) => void;
  openOrgSettings: (orgId: string, section?: OrgSettingsSection) => void;
  openProfileModal: () => void;
  closeProjectSettings: () => void;
  closeAccountSettings: () => void;
  closeProfileModal: () => void;
  closeAllModals: () => void;
  handleUrlNavigation: () => boolean;
}

const SettingsModalContext = createContext<SettingsModalContextType | undefined>(undefined);

interface SettingsModalProviderProps {
  children: ReactNode;
}

export const SettingsModalProvider: React.FC<SettingsModalProviderProps> = ({ children }) => {
  const [isProjectSettingsOpen, setIsProjectSettingsOpen] = useState(false);
  const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(null);
  const [initialSection, setInitialSection] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  const openProjectSettings = (workspaceId?: string, section?: string) => {
    if (workspaceId) {
      setCurrentWorkspaceId(workspaceId);
    }
    if (section) {
      setInitialSection(section);
    }
    setIsAccountSettingsOpen(false); // Close account settings if open
    setIsProjectSettingsOpen(true);
  };

  const openAccountSettings = (section?: string) => {
    setIsProjectSettingsOpen(false); // Close project settings if open
    setIsProfileModalOpen(false); // Close profile modal if open
    if (section) {
      // Reset to null first, then set to section to force effect to run
      setInitialSection(null);
      Promise.resolve().then(() => setInitialSection(section));
    }
    setIsAccountSettingsOpen(true);
  };

  const openProfileModal = () => {
    setIsProjectSettingsOpen(false); // Close project settings if open
    setIsAccountSettingsOpen(false); // Close account settings if open
    setIsProfileModalOpen(true);
  };

  const openOrgSettings = (orgId: string, section?: OrgSettingsSection) => {
    setIsProjectSettingsOpen(false);
    setIsAccountSettingsOpen(false);
    setIsProfileModalOpen(false);
    navigate(`/organizations/${orgId}/settings${section ? `/${section}` : ''}`);
  };

  const closeProjectSettings = () => {
    setIsProjectSettingsOpen(false);
    setCurrentWorkspaceId(null);
  };

  const closeAccountSettings = () => {
    setIsAccountSettingsOpen(false);
    setInitialSection(null);
  };

  const closeProfileModal = () => {
    setIsProfileModalOpen(false);
  };

  const closeAllModals = () => {
    setIsProjectSettingsOpen(false);
    setIsAccountSettingsOpen(false);
    setIsProfileModalOpen(false);
    setCurrentWorkspaceId(null);
    setInitialSection(null);
  };

  
  /**
   * Validates if a section name is valid for the account settings modal
   * Using dynamic section IDs from the config
   */
  const isValidAccountSection = (section: string | null): section is string => {
    if (!section) return false;
    return getAccountSectionIds().includes(section);
  };

  /**
   * Validates if a section name is valid for the project settings modal
   * Using dynamic section IDs from the config
   */
  const isValidProjectSection = (section: string | null): section is string => {
    if (!section) return false;
    return getProjectSectionIds().includes(section);
  };

  /**
   * Validates if a section name is valid for the organization settings modal
   */
  const isValidOrgSettingsSection = (section: string | null): section is OrgSettingsSection => {
    if (!section) return false;
    return ['members', 'integrations'].includes(section);
  };

  /**
   * Handles modal navigation from URL parameters
   * ?modal=account&section=credits
   * ?modal=project&section=general
   * Only opens modals if user is authenticated
   */
  const handleUrlNavigation = useCallback(() => {
    const modalType = searchParams.get('modal');
    const sectionName = searchParams.get('section');
    
    if (!modalType) return false;
    
    // Don't open modals if user is not authenticated or still loading
    // Keep the query params in URL so they can be processed after login
    if (!isAuthenticated || isLoading) {
      return false;
    }
    
    // Clean up URL regardless of validity
    const newSearchParams = new URLSearchParams(searchParams);
    if (newSearchParams.has('modal')) newSearchParams.delete('modal');
    if (newSearchParams.has('section')) newSearchParams.delete('section');
    
    // Handle modal opening based on type
    switch (modalType) {
      case 'account':
        openAccountSettings(isValidAccountSection(sectionName) ? sectionName : undefined);
        window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? `?${newSearchParams}` : ''}`);
        return true;
        
      case 'project': {
        // Extract workspace ID from URL path if we're on a project page
        const pathParts = window.location.pathname.split('/');
        const isProjectPage = pathParts.length >= 3 && pathParts[1] === 'project';
        const workspaceIdFromPath = isProjectPage ? pathParts[2] : null;
        
        // Use workspace ID from path or from query parameter as fallback
        const workspaceId = workspaceIdFromPath || searchParams.get('workspace_id');
        
        if (workspaceId) {
          // Clean up workspace_id query param if present
          if (newSearchParams.has('workspace_id')) newSearchParams.delete('workspace_id');
          
          // Open project settings with the extracted workspace ID
          openProjectSettings(workspaceId, isValidProjectSection(sectionName) ? sectionName : undefined);
          window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? `?${newSearchParams}` : ''}`);
          return true;
        }
        break;
      }
        
      case 'org-settings': {
        // Get organization ID from query parameter
        const orgId = searchParams.get('org');
        if (newSearchParams.has('org')) newSearchParams.delete('org');
        
        if (orgId) {
          openOrgSettings(orgId, isValidOrgSettingsSection(sectionName) ? sectionName : undefined);
          window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? `?${newSearchParams}` : ''}`);
          return true;
        }
        break;
      }
    }
    
    // If we get here, either the modal type was invalid or required parameters were missing
    window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? `?${newSearchParams}` : ''}`);
    return false;
  }, [searchParams, isAuthenticated, isLoading]);

  // Process URL parameters for modal navigation when the component mounts or when auth state changes
  useEffect(() => {
    handleUrlNavigation();
  }, [handleUrlNavigation, isAuthenticated, isLoading]);

  const value: SettingsModalContextType = {
    isProjectSettingsOpen,
    isAccountSettingsOpen,
    isProfileModalOpen,
    currentWorkspaceId,
    initialSection,
    openProjectSettings,
    openAccountSettings,
    openOrgSettings,
    openProfileModal,
    closeProjectSettings,
    closeAccountSettings,
    closeProfileModal,
    closeAllModals,
    handleUrlNavigation,
  };

  return (
    <SettingsModalContext.Provider value={value}>
      {children}
    </SettingsModalContext.Provider>
  );
};

export const useSettingsModal = (): SettingsModalContextType => {
  const context = useContext(SettingsModalContext);
  if (!context) {
    throw new Error('useSettingsModal must be used within a SettingsModalProvider');
  }
  return context;
};
